import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

import { TransientProps } from '@common/types';

// ── Types ───────────────────────────────────────────────

type CardContainerProps = TransientProps<{ active: boolean }>;
type CardVisualProps = TransientProps<{ hasImage: boolean }>;
type StatusBadgeProps = TransientProps<{
  variant: 'live' | 'upcoming' | 'past';
}>;

// ── Card Container ──────────────────────────────────────

export const CardContainer = styled.div<CardContainerProps>`
  ${({ theme, $active: isActive }) => css`
    border-radius: 1.25rem;
    overflow: hidden;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    ${!isActive &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}

    :hover {
      transform: translateY(-6px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }
  `}
`;

export const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
`;

// ── Visual Area ─────────────────────────────────────────

export const CardVisual = styled.div<CardVisualProps>`
  ${({ theme, $hasImage }) => css`
    height: 200px;
    position: relative;
    overflow: hidden;

    ${!$hasImage &&
    css`
      background: linear-gradient(
        145deg,
        ${theme.colors.brand.primary.default} 0%,
        ${theme.colors.brand.primary.hover} 50%,
        ${theme.colors.brand.primary.active} 100%
      );
    `}
  `}
`;

export const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CardImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 60%);
`;

export const SvgPattern = styled.svg`
  position: absolute;
  inset: 0;
  opacity: 0.06;
`;

// ── Status Badge ────────────────────────────────────────

const roomStatusTheme = {
  live: { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)' },
  upcoming: { color: '#e5a00d', bg: 'rgba(229, 160, 13, 0.12)' },
  past: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)' },
};

export const StatusBadge = styled.div<StatusBadgeProps>`
  ${({ $variant }) => css`
    position: absolute;
    top: 0.875rem;
    left: 0.875rem;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    padding: 0.25rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    background: ${roomStatusTheme[$variant].bg};
    color: ${roomStatusTheme[$variant].color};
    backdrop-filter: blur(8px);
  `}
`;

export const BadgePulseDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #dc2626;
  animation: ${keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  `} 2s infinite;
`;

// ── Audio Wave (live) ───────────────────────────────────

const waveBar = keyframes`
  0%, 100% { height: 0.5rem; }
  50% { height: 1.125rem; }
`;

export const AudioWave = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  margin-bottom: 0.875rem;
`;

export const WaveBarElement = styled.div<{ $delay: number }>`
  ${({ $delay }) => css`
    width: 3px;
    height: 0.75rem;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.5);
    animation: ${waveBar} 1.2s ease-in-out infinite;
    animation-delay: ${$delay}s;
  `}
`;

// ── Clock Circle (upcoming) ─────────────────────────────

export const ClockCircle = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
`;

export const DateCircle = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  color: white;

  > span:first-child {
    font-size: 0.8125rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  > span:last-child {
    font-size: 1.25rem;
    font-weight: 800;
    letter-spacing: 0.5px;
  }
`;

// ── Center Content ──────────────────────────────────────

export const CenterContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

// ── Enter Button (hover) ────────────────────────────────

export const EnterButton = styled.div`
  position: absolute;
  bottom: 0.875rem;
  left: 0.875rem;
  right: 0.875rem;
  z-index: 4;
  opacity: 0;
  transform: translateY(8px);
  transition: all 0.35s ease;

  ${CardContainer}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  > button {
    width: 100%;
    padding: 0.625rem 0;
    border-radius: 0.625rem;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    font-size: 0.8125rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
  }
`;

// ── Card Info ───────────────────────────────────────────

export const CardInfo = styled.div`
  padding: 1.125rem 1.25rem 1.25rem;
`;

export const CardInfoHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

export const CardInfoLeft = styled.div`
  min-width: 0;
  flex: 1;
`;

export const RoomName = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.1875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const RoomDescription = styled.div`
  ${({ theme }) => css`
    font-size: 0.75rem;
    font-weight: 0;
    color: ${theme.colors.foreground[60]};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

type TypeRoomChipProps = { $chipColor: string };

export const TypeRoomChip = styled.span<TypeRoomChipProps>`
  ${({ $chipColor }) => css`
    display: inline-block;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.1875rem 0.5rem;
    border-radius: 0.25rem;
    background: ${$chipColor}15;
    color: ${$chipColor};
    margin-bottom: 0.25rem;
  `}
`;

export const SessionBadge = styled.div`
  ${({ theme }) => css`
    flex-shrink: 0;
    padding: 0.25rem 0.625rem;
    border-radius: 0.5rem;
    background: ${theme.colors.brand.primary.opaque};
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
  `}
`;

// ── Card Footer ─────────────────────────────────────────

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SpeakersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const SpeakersLabel = styled.span`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.foreground[40]};
  margin-left: 0.25rem;
`;

export const CapacityInfo = styled.div`
  ${({ theme }) => css`
    font-size: 0.625rem;
    color: ${theme.colors.foreground[40]};
    display: flex;
    align-items: center;
    gap: 0.25rem;
  `}
`;

export const ScheduleDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #e5a00d;
  margin-bottom: 0.75rem;
  padding: 0.3125rem 0.625rem;
  background: rgba(229, 160, 13, 0.08);
  border-radius: 0.375rem;
  width: fit-content;
`;

// ── Skeleton ────────────────────────────────────────────

export const SkeletonVisual = styled.div`
  ${({ theme }) => css`
    height: 200px;
    background: ${theme.colors.background[60]};
  `}
`;

export const SkeletonInfo = styled.div`
  padding: 1.125rem 1.25rem 1.25rem;
`;
